import { Call, Device } from "@twilio/voice-sdk";
import { checkIsCallTask, checkReservationExists, checkWorker, getReservationsByTask } from "../../ActionUtils";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { Action } from "~/modules/actions/Action";
import { VoiceCall } from "~/modules/VoiceCall/VoiceCall";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface GetCallByTaskOptions {
    voiceDevice?: Device;
}































export class GetCallByTask implements Action<Promise<VoiceCall>> {
    


    readonly #taskSid: string;

    


    readonly #options?: GetCallByTaskOptions;

    constructor(taskSid: string, options?: GetCallByTaskOptions) {
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        const voiceController = context.retrieveInstanceOf(ContextInstance.VoiceControllerImpl, VoiceControllerImpl);
        if (this.#options?.voiceDevice) {
            await voiceController.init(this.#options.voiceDevice);
        }
        await cbm.init();

        logger.debug(`getCallByTask invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;

        checkWorker(worker, {
            actionName: "GetCallByTask",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.GetCallByTaskCompleted,
            extra: {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            }
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "pending" || res.status === "accepted");

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "GetCallByTask",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.GetCallByTaskCompleted,
            extra: {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            }
        });

        const validReservation = reservation!;

        checkIsCallTask(validReservation.task, {
            actionName: "GetCallByTask",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.GetCallByTaskCompleted,
            extra: {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            }
        });

        
        const activeCall: Call | undefined = voiceController.voiceDevice?.["_activeCall"];

        if (activeCall?.customParameters?.get("flex_task_sid") === this.#taskSid) {
            sendWorkerTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice,
                callSid: activeCall.parameters?.CallSid ?? "",
                isIncomingCall: false
            });
            return new VoiceCallImpl(context, activeCall, voiceController.voiceDevice) as VoiceCall;
        }

        const incomingCall = voiceController.voiceDevice?.calls?.find(
            (call) => call.customParameters?.get("flex_task_sid") === this.#taskSid
        );
        if (incomingCall) {
            sendWorkerTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice,
                callSid: incomingCall.parameters?.CallSid ?? "",
                isIncomingCall: true
            });
            return new VoiceCallImpl(context, incomingCall, voiceController.voiceDevice) as VoiceCall;
        }

        const errorMsg = `getCallByTask: Failed to get call by task ${
            this.#taskSid
        }. The call might be active on a different voice device`;
        logger.error(errorMsg);
        sendWorkerTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
            error: errorMsg,
            taskSid: this.#taskSid,
            customVoiceDevice: !!this.#options?.voiceDevice
        });
        return Promise.reject(
            createFlexSdkError(context)(ErrorCode.FailedToGetCallByTask, { severity: ErrorSeverity.Error }, errorMsg)
        );
    }
}
